import type {PageProps} from 'gatsby'
import {graphql} from 'gatsby'
import React from 'react'

import Footer from '@/components/footer'
import Header from '@/components/header'
import SEO from '@/components/seo'
import {Main} from '@/components/skip-to-content'
import type {PricingPageQuery} from '@/graphql-types'
import locales from '@/locale/locales'
import {PricingSection} from '@/sections/pricing'
import type {GatsbyHeadProps} from '@/types/gatsby-head'
import handleNoPrismicData from '@/utils/handle-no-prismic-data'
import {withPrismicPreview} from '@/utils/with-prismic-preview'

import {PricingPage, dataTestId} from './pricing-page'

type Props = PageProps<PricingPageQuery>

const PricingTemplate = (props: Props) => {
    const data = props.data.prismicPricingPage.data

    if (!data) {
        return handleNoPrismicData()
    }

    return (
        <>
            <Header />

            <Main>
                <PricingPage data={data}>
                    <PricingSection dataTestId={`${dataTestId}-cards`} withTitleImage={false} />
                </PricingPage>
            </Main>

            <Footer />
        </>
    )
}

export const Head = ({location, pageContext, data}: GatsbyHeadProps<PricingPageQuery>) => {
    const pageData = data.prismicPricingPage.data

    // Can't access React Context here (until gatsby@5.6.0), so useLocale() doesn't work
    const locale = locales.find((locale) => {
        return locale.code === pageContext.locale
    })

    return (
        <SEO
            location={location}
            pageContext={pageContext}
            title={pageData.meta_title}
            description={pageData.meta_description}
            openGraphImageUrl={pageData.open_graph_image?.url}
            preventIndexing={pageData.exclude_robots}
            canonicalLocation={`/${locale?.urlCode}/pricing`}
        />
    )
}

export const query = graphql`
    query PricingPage($locale: String!, $uid: String!) {
        prismicPricingPage(uid: {eq: $uid}, lang: {eq: $locale}) {
            _previewable
            data {
                meta_title
                meta_description
                exclude_robots
                open_graph_image {
                    alt
                    copyright
                    url
                    dimensions {
                        height
                        width
                    }
                }
                title_image {
                    alt
                    copyright
                    url
                    dimensions {
                        height
                        width
                    }
                }
                title {
                    richText
                }
                title_image {
                    alt
                    copyright
                    url
                    dimensions {
                        height
                        width
                    }
                }
                tables_title
                tables_tick_screen_reader_alt
                tables {
                    table {
                        document {
                            ...PricingTable
                        }
                    }
                }
                questions_title
                questions_answers {
                    question
                    answer {
                        richText
                    }
                }
                testimonial {
                    document {
                        ... on PrismicTestimonial {
                            id
                            data {
                                image {
                                    alt
                                    copyright
                                    url
                                    dimensions {
                                        height
                                        width
                                    }
                                }
                                quote
                                attribution_name
                                attribution_title
                                cta_text
                            }
                        }
                    }
                }
                social_proof_logos_extended_section {
                    document {
                        ... on PrismicSocialProofLogosExtendedSection {
                            data {
                                title {
                                    richText
                                }
                                logos {
                                    logo {
                                        alt
                                        copyright
                                        url
                                        dimensions {
                                            height
                                            width
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                social_proof_ratings_section {
                    document {
                        ... on PrismicSocialProofRatingsSection {
                            data {
                                title {
                                    richText
                                }
                                ratings {
                                    rating {
                                        document {
                                            ... on PrismicRating {
                                                data {
                                                    logo {
                                                        alt
                                                        copyright
                                                        url
                                                        dimensions {
                                                            height
                                                            width
                                                        }
                                                    }
                                                    stars
                                                    rating_number
                                                    link
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                why_pleo {
                    pre_title
                    image {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    title
                    body {
                        richText
                    }
                }
                signup_banner {
                    document {
                        ...SignupBanner
                    }
                }
            }
        }
    }
`

export default withPrismicPreview(PricingTemplate)
